import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

import styled from '@/styles';

// Design values from the Lifecycle journeys prototype
// (GravyWorkers Prototype.dc.html). Kept in one place so every card, row and
// button reads the same numbers.
export const C = {
  black: 'rgb(28,28,28)',
  grey: 'rgb(91,91,91)',
  light: 'rgb(151,151,151)',
  stroke: 'rgb(218,218,218)',
  fieldStroke: 'rgb(151,151,151)',
  green: 'rgb(46,135,34)',
  greenFill: 'rgb(68,167,53)',
  greenLine: 'rgb(167,218,161)',
  greenSoft: 'rgb(249,252,249)',
  greenHover: 'rgb(232,245,230)',
  optionHover: 'rgb(244,248,243)',
  amber: 'rgb(187,135,0)',
  blue: 'rgb(71,133,255)',
  red: 'rgb(207,4,12)',
  page: 'rgb(252,252,252)',
  white: 'rgb(255,255,255)',
  badgeGreen: 'rgb(231,244,230)',
  badgeAmber: 'rgb(255,244,216)',
  scriptBg: 'rgb(250,250,250)',
};

export const FONT = 'Manrope, sans-serif';

export const cardStyle: CSSProperties = {
  background: C.white,
  borderRadius: 12,
  boxShadow: `inset 0 0 0 1px ${C.stroke}`,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
};

export const text14: CSSProperties = { fontSize: 14, lineHeight: 1.4 };

// Heroicons outline paths used by the design.
export const PATHS = {
  sms: 'M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z',
  email:
    'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75',
  call: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z',
  push: 'M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0',
  back: 'M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18',
  close: 'M6 18 18 6M6 6l12 12',
  chevronUp: 'm4.5 15.75 7.5-7.5 7.5 7.5',
  chevronRight: 'm9.5 7 5 5-5 5',
  chevronDown: 'm17 9.5-5 5-5-5',
  info: 'm11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
  refresh:
    'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99',
  arrowUp: 'M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18',
  check: 'm4.5 12.75 6 6 9-13.5',
  arrowRight: 'M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3',
};

export const CHECK_SOLID =
  'M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z';

type IconProps = {
  d: string;
  size?: number;
  color?: string;
  width?: number;
  style?: CSSProperties;
};

export const Icon = ({
  d,
  size = 24,
  color = C.black,
  width = 1.5,
  style,
}: IconProps) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    stroke={color}
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={width}
    style={{ flexShrink: 0, ...style }}
    viewBox="0 0 24 24"
    width={size}
  >
    <path d={d} />
  </svg>
);

export const SolidCheck = () => (
  <svg
    aria-hidden="true"
    fill={C.green}
    height="16"
    viewBox="0 0 20 20"
    width="16"
  >
    <path clipRule="evenodd" d={CHECK_SOLID} fillRule="evenodd" />
  </svg>
);

// assets/mobile/lifecycle-journey.svg from the design.
export const LifecycleJourneyIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="20"
    viewBox="0 0 20 20"
    width="20"
  >
    <g
      stroke={C.black}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.25"
    >
      <path d="M9 11.5L7.74699 3.98192C7.62957 3.27743 8.26721 2.68052 8.96242 2.8441L13.853 3.99483C14.6896 4.19168 14.9046 5.28243 14.2052 5.78198L9 9.5" />
      <path d="M3 17C3 16.7365 4.83334 14.8931 6.32594 13.2404C7.02192 12.4698 8.00294 12 9.04133 12C10.0322 12 10.9751 12.4263 11.6297 13.1701L15 17" />
      <path d="M5 15L5.6934 16.7335C5.8329 17.0823 6.2928 17.1584 6.53726 16.8732L8.24074 14.8858C8.63984 14.4202 9.36016 14.4202 9.75926 14.8858L11.4627 16.8732C11.7072 17.1584 12.1671 17.0823 12.3066 16.7335L13 15" />
    </g>
  </svg>
);

export const Dot = () => (
  <span
    aria-hidden="true"
    style={{
      width: 4,
      height: 4,
      borderRadius: '50%',
      background: C.black,
      flexShrink: 0,
    }}
  />
);

export const InfoLine = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      color: C.amber,
      marginTop: 'auto',
    }}
  >
    <Icon color={C.amber} d={PATHS.info} size={20} />
    <span>{children}</span>
  </div>
);

// A plain <button> reset, so clickable rows and icons keep keyboard access
// without picking up the app's button styles.
export const Plain = styled('button', {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
  '&:disabled': { cursor: 'not-allowed' },
});

const buttonBase = {
  height: 36,
  padding: '8px 16px',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FONT,
  fontSize: 14,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
} as const;

// Filled green; grey and not-allowed when disabled.
export const FilledButton = styled('button', {
  ...buttonBase,
  fontWeight: 700,
  background: C.greenFill,
  color: C.white,
  boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.12)',
  '&:disabled': {
    background: C.stroke,
    color: C.light,
    cursor: 'not-allowed',
  },
});

export const OutlineButton = styled('button', {
  ...buttonBase,
  fontWeight: 600,
  background: C.white,
  color: C.green,
  boxShadow: `inset 0 0 0 1px ${C.greenFill}`,
  '&:hover:not(:disabled)': { background: C.optionHover },
  '&:disabled': {
    color: C.light,
    boxShadow: `inset 0 0 0 1px ${C.stroke}`,
    cursor: 'not-allowed',
  },
});

export const TextButton = styled('button', {
  ...buttonBase,
  fontWeight: 600,
  background: 'none',
  color: C.green,
  '&:disabled': { color: C.light, cursor: 'not-allowed' },
});

export const Toggle = ({
  on,
  disabled,
  label,
  title,
  onClick,
}: {
  on: boolean;
  disabled?: boolean;
  label: string;
  title?: string;
  onClick: () => void;
}) => (
  <Plain
    aria-checked={on}
    aria-label={label}
    data-testid="automatedMessagesToggle"
    disabled={disabled}
    role="switch"
    style={{
      width: 44,
      height: 24,
      borderRadius: 100,
      background: on ? C.greenFill : C.stroke,
      position: 'relative',
      flexShrink: 0,
      transition: 'background .15s',
      opacity: disabled ? 0.6 : 1,
    }}
    title={title}
    type="button"
    onClick={onClick}
  >
    <span
      style={{
        position: 'absolute',
        top: 2,
        left: on ? 22 : 2,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: C.white,
        boxShadow:
          '0px 1.765px 4.706px 0px rgba(0,0,0,0.15), 0px 1.765px 0.588px 0px rgba(0,0,0,0.06)',
        transition: 'left .15s',
      }}
    />
  </Plain>
);

export const Badge = ({ draft }: { draft: boolean }) => (
  <span
    data-testid="copyStatusBadge"
    style={{
      padding: '2px 8px',
      borderRadius: 8,
      fontWeight: 600,
      fontSize: 14,
      lineHeight: 1.4,
      background: draft ? C.badgeAmber : C.badgeGreen,
      color: draft ? C.amber : C.green,
    }}
  >
    {draft ? 'Draft' : 'Published'}
  </span>
);

const Option = styled('button', {
  appearance: 'none',
  border: 'none',
  font: 'inherit',
  height: 40,
  padding: '0 12px',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 14,
  lineHeight: 1.4,
  color: C.black,
  cursor: 'pointer',
  background: 'transparent',
  textAlign: 'left',
  '&[aria-selected="true"]': { background: C.optionHover, fontWeight: 600 },
  '&:hover': { background: C.optionHover },
});

type SelectOption = { value: string; label: string };

// The design's dropdown: a 36px field and a list whose picked option is
// shaded and checked. Multiple keeps the list open and toggles.
export const DesignSelect = ({
  label,
  testId,
  options,
  value,
  placeholder = 'Select one',
  multiple = false,
  disabled = false,
  openUp = false,
  onChange,
}: {
  label: string;
  testId: string;
  options: SelectOption[];
  value: string[];
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  openUp?: boolean;
  onChange: (value: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const picked = options.filter((o) => value.includes(o.value));
  const shown = picked.map((o) => o.label).join(', ');

  return (
    <div
      ref={box}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
        minWidth: 0,
      }}
      onKeyDown={(event) => {
        if (open && event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
        }
      }}
    >
      <span style={{ ...text14, fontWeight: 600, color: C.black }}>
        {label}
      </span>
      <Plain
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        style={{
          minHeight: 36,
          borderRadius: 8,
          background: C.white,
          boxShadow: `inset 0 0 0 1px ${C.stroke}`,
          padding: '7px 8px 7px 16px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          ...text14,
        }}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ color: shown ? C.black : C.light, minWidth: 0 }}>
          {shown || placeholder}
        </span>
        <Icon d={PATHS.chevronDown} />
      </Plain>
      {open && (
        <div
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          role="listbox"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            ...(openUp ? { bottom: 44 } : { top: 64 }),
            background: C.white,
            borderRadius: 8,
            boxShadow: `0 0 0 1px ${C.stroke}, 0 8px 24px rgba(0,0,0,0.08)`,
            padding: 4,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            zIndex: 30,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {options.map((option) => {
            const on = value.includes(option.value);
            return (
              <Option
                aria-selected={on}
                key={option.value}
                role="option"
                type="button"
                onClick={() => {
                  if (multiple) {
                    onChange(
                      on
                        ? value.filter((v) => v !== option.value)
                        : [...value, option.value],
                    );
                  } else {
                    onChange([option.value]);
                    setOpen(false);
                  }
                }}
              >
                <span>{option.label}</span>
                {on && <SolidCheck />}
              </Option>
            );
          })}
        </div>
      )}
    </div>
  );
};
